/** Native-shell clientId bridge (AndroidInterface / Capacitor NativeBridge). */

export const NATIVE_CLIENT_ID_TIMEOUT_MS = 1000;
const BRIDGE_POLL_MS = 50;

function getCapacitorNativeBridge() {
  if (typeof window === 'undefined') {
    return null;
  }
  return window.Capacitor?.Plugins?.NativeBridge ?? null;
}

function getAndroidInterface() {
  if (typeof window === 'undefined') {
    return null;
  }
  return window.AndroidInterface ?? null;
}

/** True when either native bridge surface is present. */
export function hasNativeClientIdBridge() {
  const android = getAndroidInterface();
  if (android && typeof android.getStoredClientId === 'function') {
    return true;
  }
  const bridge = getCapacitorNativeBridge();
  return Boolean(bridge && typeof bridge.getClientId === 'function');
}

/**
 * True when a bridge object exists but lacks the clientId methods, i.e. an app build
 * released before native clientId storage. Waiting for it would never succeed.
 */
function hasOutdatedNativeShell() {
  const android = getAndroidInterface();
  if (android) {
    return typeof android.getStoredClientId !== 'function';
  }
  const bridge = getCapacitorNativeBridge();
  if (bridge) {
    return typeof bridge.getClientId !== 'function';
  }
  return false;
}

/**
 * Poll until AndroidInterface / NativeBridge is injected (Android adds JS interface
 * asynchronously after onCreate). Returns false immediately on outdated app builds.
 */
export function waitForNativeBridge(timeoutMs = NATIVE_CLIENT_ID_TIMEOUT_MS) {
  if (hasNativeClientIdBridge()) {
    return Promise.resolve(true);
  }
  if (hasOutdatedNativeShell()) {
    return Promise.resolve(false);
  }
  return new Promise(resolve => {
    const started = Date.now();
    const timer = setInterval(() => {
      if (hasNativeClientIdBridge()) {
        clearInterval(timer);
        resolve(true);
        return;
      }
      if (Date.now() - started >= timeoutMs) {
        clearInterval(timer);
        resolve(false);
      }
    }, BRIDGE_POLL_MS);
  });
}

/**
 * Read clientId from native storage. Resolves to raw string or null.
 * Race against timeoutMs; late callers may still get a value after timeout via the same promise.
 */
export function getNativeClientId(timeoutMs = NATIVE_CLIENT_ID_TIMEOUT_MS) {
  const readPromise = (async () => {
    await waitForNativeBridge(timeoutMs);
    const android = getAndroidInterface();
    if (android && typeof android.getStoredClientId === 'function') {
      try {
        const value = android.getStoredClientId();
        return value == null || value === '' ? null : String(value);
      } catch (_e) {
        return null;
      }
    }
    const bridge = getCapacitorNativeBridge();
    if (bridge && typeof bridge.getClientId === 'function') {
      try {
        const result = await bridge.getClientId();
        const value = result?.clientId;
        return value == null || value === '' ? null : String(value);
      } catch (_e) {
        return null;
      }
    }
    return null;
  })();

  const timeoutPromise = new Promise(resolve => {
    setTimeout(() => resolve(null), timeoutMs);
  });

  // Expose the underlying read so bootstrap can reconcile late responses.
  const raced = Promise.race([readPromise, timeoutPromise]);
  raced.readPromise = readPromise;
  return raced;
}

/** Fire-and-forget write to native storage. Errors are swallowed. */
export function setNativeClientId(value) {
  if (!value) {
    return;
  }
  try {
    const android = getAndroidInterface();
    if (android && typeof android.setStoredClientId === 'function') {
      android.setStoredClientId(String(value));
      return;
    }
    const bridge = getCapacitorNativeBridge();
    if (bridge && typeof bridge.setClientId === 'function') {
      Promise.resolve(bridge.setClientId({ clientId: String(value) })).catch(() => {
        // ignore native write failures
      });
    }
  } catch (_e) {
    // ignore native write failures
  }
}
